import React, { useMemo } from "react";

import {
  TaskPriority,
  TaskStatus,
} from "@fartherfinance/frontend/api/Tasks/Types";
import { TransitionType } from "@fartherfinance/frontend/api/TransitionWorkflows/Types";

import {
  createVirtualTaskId,
  VirtualTask,
} from "@src/multiCustodian/pages/Advisor/components/VirtualSubtasks/types";

import {
  getAssigneeForTransitionType,
  getChildrenCatalogForTransitionType,
  getRootTaskDefinitionsForTransitionType,
  getServiceTeamForTransitionType,
} from "./constants";
import VirtualSubtasks from "./VirtualSubtasks";

// --- Helpers --------------------------------------------------------------
const generateId = (): string => Math.random().toString(36).substring(2, 10);

const createTaskFromConfig = (
  configTask: any,
  isRoot: boolean,
  children: VirtualTask[] = []
): VirtualTask => ({
  id: configTask.id || createVirtualTaskId(generateId()),
  title: configTask.title,
  taskStatus: TaskStatus.enum.NOT_STARTED,
  children,
  isRoot,
  description: configTask.description || "",
  dueDate: null,
  taskPriority: TaskPriority.enum.MEDIUM,
  taskType: configTask.taskType, // Preserve taskType from config
  serviceTeam: configTask.serviceTeam || null,
  assignee: configTask.assignee || null,
});

const createTask = (
  title: string,
  isRoot: boolean,
  children: VirtualTask[] = [],
  id?: string,
  transitionType?: TransitionType
): VirtualTask => {
  const defaultServiceTeam = transitionType
    ? getServiceTeamForTransitionType(transitionType)
    : null;
  const defaultAssignee = transitionType
    ? getAssigneeForTransitionType(transitionType)
    : null;

  return {
    id: createVirtualTaskId(id ?? generateId()),
    title,
    taskStatus: TaskStatus.enum.NOT_STARTED,
    children,
    isRoot,
    description: "",
    dueDate: null,
    taskPriority: TaskPriority.enum.MEDIUM,
    serviceTeam: defaultServiceTeam,
    assignee: defaultAssignee,
  };
};

const generateChildrenForRoot = (
  rootTitle: string,
  transitionType: TransitionType
): VirtualTask[] => {
  const childrenCatalog = getChildrenCatalogForTransitionType(transitionType);
  const childrenTasks = childrenCatalog[rootTitle] ?? [];

  // Convert config tasks to VirtualTask objects, preserving all properties from config
  return childrenTasks.map((childTask: any) =>
    createTaskFromConfig(childTask, false, [])
  );
};

// Returns the predefined root tasks for the specific transition type. Each root task starts with
// its predefined children from the configuration.
const createInitialRootTasks = (
  transitionType: TransitionType
): VirtualTask[] => {
  const rootTaskDefinitions =
    getRootTaskDefinitionsForTransitionType(transitionType);
  return rootTaskDefinitions.map((def) =>
    createTask(
      def.title,
      true,
      generateChildrenForRoot(def.title, transitionType),
      def.id,
      transitionType
    )
  );
};

export const createInitialVirtualTasks = (
  transitionType: TransitionType
): VirtualTask[] => createInitialRootTasks(transitionType);

interface TransitionVirtualSubtasksSectionProps {
  transitionType: TransitionType;
  initialTasks?: VirtualTask[];
  onChange?: (tasks: VirtualTask[]) => void;
}

const TransitionVirtualSubtasksSection: React.FC<
  TransitionVirtualSubtasksSectionProps
> = ({ transitionType, initialTasks, onChange }) => {
  const tasks = useMemo(() => {
    return initialTasks || createInitialVirtualTasks(transitionType);
  }, [initialTasks, transitionType]);

  return (
    <VirtualSubtasks
      transitionType={transitionType}
      tasks={tasks}
      onChange={onChange || (() => {})}
    />
  );
};

export default TransitionVirtualSubtasksSection;
